/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ArrowUp, ArrowDown } from 'react-feather';
import DateFormat from 'constants/date_format';
import InfoTooltip from 'components/shared/InfoTooltip';
import BootstrapTooltip from 'components/shared/BootstrapTooltip';
import ProgressIndicator from './ProgressIndicator';

const ReportingTable = ({ data, type }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [sortedData, setSortedData] = useState(data);

  const sortAlphaUp = (sortData, property) => {
    const sorted = [...sortData].sort((a, b) =>
      a[`${property}`].localeCompare(b[`${property}`]),
    );

    setSortedData(sorted);

    return sorted;
  };

  const sortAlphaDown = (sortData, property) => {
    const sorted = sortAlphaUp(sortData, property);
    sorted.reverse();

    setSortedData(sorted);

    return sorted;
  };

  const sortDateUp = (sortData, property) => {
    const sorted = [...sortData].sort(
      (a, b) => new Date(b[`${property}`]) - new Date(a[`${property}`]),
    );

    setSortedData(sorted);

    return sorted;
  };

  const sortDateDown = (sortData, property) => {
    const sorted = sortDateUp(sortData, property);
    sorted.reverse();

    setSortedData(sorted);

    return sorted;
  };

  const sortNumericUp = (sortData, property) => {
    const sorted = [...sortData].sort(
      (a, b) => b[`${property}`] - a[`${property}`],
    );

    setSortedData(sorted);

    return sorted;
  };

  const sortNumericDown = (sortData, property) => {
    const sorted = sortNumericUp(sortData, property);
    sorted.reverse();

    setSortedData(sorted);

    return sorted;
  };

  const handleSort = column => {
    if (column !== sortColumn) {
      setSortDirection('up');
    } else if (!sortDirection) {
      setSortDirection('up');
    } else if (sortDirection === 'up') {
      setSortDirection('down');
    } else {
      setSortDirection(null);
    }

    setSortColumn(column);
  };

  const handleSortType = () => {
    if (sortDirection === 'up') {
      if (sortColumn === 'name') {
        sortAlphaUp(data, sortColumn);
      } else if (sortColumn === 'progress') {
        sortNumericUp(data, sortColumn);
      } else {
        sortDateUp(data, sortColumn);
      }
    } else if (sortDirection === 'down') {
      if (sortColumn === 'name') {
        sortAlphaDown(data, sortColumn);
      } else if (sortColumn === 'progress') {
        sortNumericDown(data, sortColumn);
      } else {
        sortDateDown(data, sortColumn);
      }
    }
  };

  useEffect(() => {
    handleSortType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    setSortColumn(null);
    setSortDirection(null);
    setSortedData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const toLocalDate = date => {
    return moment(moment(date).format(DateFormat.fixed.ISO8601_LOCAL) + 'Z');
  };

  const formattedLocalDate = date => {
    return toLocalDate(date).format(DateFormat.MONTH_DAY_YEAR_SHORT);
  };

  const checkOverdueStatus = date => {
    if (type === 'overdue') return true;
    if (type === 'complete') return false;
    return date && toLocalDate(date) < new Date();
  };

  return (
    <table className="JourneyReportingTable">
      <thead className="JourneyReportingTable-head">
        <tr>
          <th
            className="JourneyReportingTable-headCell JourneyReportingTable-cell"
            onClick={() => handleSort('name')}
          >
            <div className="JourneyReportingTable-cellContent">
              {t('journeys.reporting.table_headers.name')}
              {sortColumn === 'name' && sortDirection === 'up' && (
                <ArrowUp size={14} className="JourneyReportingTable-icon" />
              )}
              {sortColumn === 'name' && sortDirection === 'down' && (
                <ArrowDown size={14} className="JourneyReportingTable-icon" />
              )}
            </div>
          </th>
          <th
            className="JourneyReportingTable-headCell JourneyReportingTable-cell"
            onClick={() => handleSort('started_at')}
          >
            <div className="JourneyReportingTable-cellContent">
              {t('journeys.reporting.table_headers.started')}
              {sortColumn === 'started_at' && sortDirection === 'up' && (
                <ArrowUp size={14} className="JourneyReportingTable-icon" />
              )}
              {sortColumn === 'started_at' && sortDirection === 'down' && (
                <ArrowDown size={14} className="JourneyReportingTable-icon" />
              )}
            </div>
          </th>
          <th
            className="JourneyReportingTable-headCell JourneyReportingTable-cell"
            onClick={() => handleSort('due_by')}
          >
            <div className="JourneyReportingTable-cellContent">
              {t('journeys.reporting.table_headers.due_by')}
              {sortColumn === 'due_by' && sortDirection === 'up' && (
                <ArrowUp size={14} className="JourneyReportingTable-icon" />
              )}
              {sortColumn === 'due_by' && sortDirection === 'down' && (
                <ArrowDown size={14} className="JourneyReportingTable-icon" />
              )}
            </div>
          </th>
          {data.length > 0 && data[0].completed_at && (
            <th
              className="JourneyReportingTable-headCell JourneyReportingTable-cell"
              onClick={() => handleSort('completed_at')}
            >
              <div className="JourneyReportingTable-cellContent">
                {t('journeys.reporting.table_headers.completed')}
                {sortColumn === 'completed_at' && sortDirection === 'up' && (
                  <ArrowUp size={14} className="JourneyReportingTable-icon" />
                )}
                {sortColumn === 'completed_at' && sortDirection === 'down' && (
                  <ArrowDown size={14} className="JourneyReportingTable-icon" />
                )}
              </div>
            </th>
          )}
          <th
            className="JourneyReportingTable-headCell JourneyReportingTable-cell"
            onClick={() => handleSort('progress')}
          >
            <div className="JourneyReportingTable-cellContent">
              {t('journeys.reporting.table_headers.progress')}
              <InfoTooltip
                tooltip={t('journeys.reporting.tooltip.progress')}
                className="ml-4"
                placement="bottom"
              />
              {sortColumn === 'progress' && sortDirection === 'up' && (
                <ArrowUp size={14} className="JourneyReportingTable-icon" />
              )}
              {sortColumn === 'progress' && sortDirection === 'down' && (
                <ArrowDown size={14} className="JourneyReportingTable-icon" />
              )}
            </div>
          </th>
        </tr>
      </thead>
      <tbody className="JourneyReportingTable-body">
        {sortDirection === null
          ? data.map(rowData => (
              <tr
                key={`${rowData.name}-${rowData.started_at}`}
                className="JourneyReportingTable-row"
              >
                <td
                  className="JourneyReportingTable-cell JourneyReportingTable-cell--clickable"
                  onClick={() =>
                    window.location.assign(
                      `/journey/${rowData.journey_id}/users/${rowData.user_id}`,
                    )
                  }
                >
                  {rowData.name}
                </td>
                <td className="JourneyReportingTable-cell">
                  {rowData.started_at
                    ? formattedLocalDate(rowData.started_at)
                    : '-'}
                </td>
                <td
                  className={
                    'JourneyReportingTable-cell ' +
                    (checkOverdueStatus(rowData.due_by) &&
                      'JourneyReportingTable-cell--overdue')
                  }
                >
                  <div className="JourneyReportingTable-cellContent">
                    {rowData.due_by ? formattedLocalDate(rowData.due_by) : '-'}
                    {checkOverdueStatus(rowData.due_by) && (
                      <BootstrapTooltip
                        className="ml-12"
                        tooltip="Overdue"
                        dataPlacement="top"
                        iconName="alert-circle"
                      />
                    )}
                  </div>
                </td>
                {rowData.completed_at && (
                  <td className="JourneyReportingTable-cell">
                    {formattedLocalDate(rowData.completed_at)}
                  </td>
                )}
                <td className="JourneyReportingTable-cell">
                  <ProgressIndicator
                    progress={rowData.progress}
                    overdue={checkOverdueStatus(rowData.due_by)}
                  />
                </td>
              </tr>
            ))
          : sortedData.map(rowData => (
              <tr
                key={`${rowData.name}-${rowData.started_at}`}
                className="JourneyReportingTable-row"
              >
                <td
                  className="JourneyReportingTable-cell JourneyReportingTable-cell--clickable"
                  onClick={() =>
                    window.location.assign(
                      `/journey/${rowData.journey_id}/users/${rowData.user_id}`,
                    )
                  }
                >
                  {rowData.name}
                </td>
                <td className="JourneyReportingTable-cell">
                  {rowData.started_at
                    ? formattedLocalDate(rowData.started_at)
                    : '-'}
                </td>
                <td className="JourneyReportingTable-cell">
                  {rowData.due_by ? formattedLocalDate(rowData.due_by) : '-'}
                </td>
                {rowData.completed_at && (
                  <td className="JourneyReportingTable-cell">
                    {formattedLocalDate(rowData.completed_at)}
                  </td>
                )}
                <td
                  className={
                    'JourneyReportingTable-cell ' +
                    (checkOverdueStatus(rowData.due_by) &&
                      'JourneyReportingTable-cell--overdue')
                  }
                >
                  <div className="JourneyReportingTable-cellContent">
                    {rowData.due_by ? formattedLocalDate(rowData.due_by) : '-'}
                    {checkOverdueStatus(rowData.due_by) && (
                      <BootstrapTooltip
                        className="ml-12"
                        tooltip="Overdue"
                        dataPlacement="top"
                        iconName="alert-circle"
                      />
                    )}
                  </div>
                </td>
                <td className="JourneyReportingTable-cell">
                  <ProgressIndicator
                    progress={rowData.progress}
                    overdue={checkOverdueStatus(rowData.due_by)}
                  />
                </td>
              </tr>
            ))}
      </tbody>
    </table>
  );
};

export default ReportingTable;
